import type { FC, ReactNode } from 'react';
import Button from 'plaid-threads/Button';
import Shield from 'plaid-threads/Icons/Shield';
import Switch from 'plaid-threads/Icons/Switch';
import styled from 'threads5/styles/styled';

import { useTrack } from 'src/contexts';
import { LOGGED_IN_DOCS } from 'src/lib/trackingEvents';

export const SIGN_IN_URL = 'https://dashboard.plaid.com/signin?redirect=docs';
export const SIGN_UP_URL = 'https://dashboard.plaid.com/signup';
export const TEAMS_URL = 'https://dashboard.plaid.com/personal/teams';
export const REQUEST_ACCESS_URL =
  'https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access';

const BLACK = '#111';
const MUTED = '#5f6163';
const BORDER = 'rgb(222, 227, 229)';
const SURFACE = '#f6f7f7';

const StyledGatedPage = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 0 64px',
});

const StyledContainer = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '560px',
  textAlign: 'center',
  width: '100%',
});

const StyledIconTile = styled('div')({
  alignItems: 'center',
  backgroundColor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '12px',
  color: BLACK,
  display: 'flex',
  height: '48px',
  justifyContent: 'center',
  marginBottom: '24px',
  width: '48px',
});

const StyledHeading = styled('h2')({
  color: BLACK,
  marginBottom: '8px',
});

const StyledPageName = styled('p')({
  color: MUTED,
  fontSize: '1.4rem',
  lineHeight: '2.2rem',
  marginBottom: '8px',
});

const StyledBody = styled('div')({
  color: MUTED,
  marginBottom: '32px',
  a: {
    color: '#0a5fd8',
    textDecoration: 'underline',
  },
});

const StyledActions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  justifyContent: 'center',
}));

const StyledTeamNote = styled('div')({
  borderTop: `1px solid ${BORDER}`,
  color: MUTED,
  marginTop: '40px',
  paddingTop: '24px',
  width: '100%',
  p: {
    fontSize: '1.4rem',
    lineHeight: '2.2rem',
    marginBottom: 0,
  },
  strong: {
    color: BLACK,
  },
  a: {
    color: BLACK,
    textDecoration: 'underline',
  },
});

const StyledTeamNoteIcon = styled('span')({
  display: 'inline-flex',
  marginBottom: '8px',
});

type Cta = { label: string; href: string };

/**
 * Shown in place of a docs page the reader can't see yet, either because they
 * are logged out or because their current team isn't enrolled in the flag that
 * gates the page. Names the page, explains who can see it and gives the next step.
 */
const GatedPage: FC<{
  isLoggedIn: boolean | null;
  section?: string;
  title?: string;
  // Page-specific explanation of who can see these docs and why
  body?: ReactNode;
  // Page-specific way to get access, e.g. a waitlist or beta sign-up form
  cta?: Cta;
  teamName?: string;
}> = ({ isLoggedIn, section, title, body, cta, teamName }) => {
  const track = useTrack();

  const heading = isLoggedIn
    ? "Your team doesn't have access to this page yet"
    : 'Log in to view this page';
  const pageName = [section, title]
    .filter((part, i, parts) => part && parts.indexOf(part) === i)
    .join(' / ');
  const fallbackBody = isLoggedIn
    ? 'This page is part of a limited availability program, and access is granted per Dashboard team.'
    : 'This page is only visible when you are logged in to the Plaid Dashboard.';

  const primary: Cta = isLoggedIn
    ? cta ?? { label: 'Request access', href: REQUEST_ACCESS_URL }
    : { label: 'Log in', href: SIGN_IN_URL };
  const secondary: Cta = isLoggedIn
    ? { label: 'Browse all docs', href: '/docs/' }
    : cta ?? { label: 'Create a free account', href: SIGN_UP_URL };

  return (
    <StyledGatedPage data-testid='gated-page'>
      <StyledContainer>
        <StyledIconTile aria-hidden='true'>
          <Shield />
        </StyledIconTile>
        {pageName ? <StyledPageName>{pageName}</StyledPageName> : null}
        <StyledHeading>{heading}</StyledHeading>
        <StyledBody>{body ?? fallbackBody}</StyledBody>

        <StyledActions>
          <Button
            href={primary.href}
            inline
            size={48}
            centered
            icon={null}
            className={isLoggedIn ? undefined : 'signup-attach-utm'}
            onClick={() => {
              if (!isLoggedIn) {
                track({
                  type: LOGGED_IN_DOCS.USER_CLICKED_LOGGED_IN_DOCS_SIGN_IN,
                });
              }
            }}
          >
            {primary.label}
          </Button>
          <Button
            href={secondary.href}
            inline
            size={48}
            centered
            secondary
            icon={null}
          >
            {secondary.label}
          </Button>
        </StyledActions>

        {isLoggedIn ? (
          <StyledTeamNote>
            <StyledTeamNoteIcon aria-hidden='true'>
              <Switch />
            </StyledTeamNoteIcon>
            <p>
              {teamName ? (
                <>
                  You&apos;re logged in to <strong>{teamName}</strong>.{' '}
                </>
              ) : null}
              If another one of your teams has access,{' '}
              <a href={TEAMS_URL}>switch teams</a> and reload this page.
              Otherwise, contact your account manager or{' '}
              <a href={REQUEST_ACCESS_URL}>request access in the Dashboard</a>.
            </p>
          </StyledTeamNote>
        ) : null}
      </StyledContainer>
    </StyledGatedPage>
  );
};

GatedPage.displayName = 'GatedPage';

export default GatedPage;
